import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Loader2, ArrowRight, Eye, EyeOff, Zap, Globe, Heart } from "lucide-react";
import { APP_NAME } from "@/lib/constants";
import { GoogleAuthButton } from "@/components/auth/GoogleAuthButton";

const Register = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [fullName, setFullName] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showPassword, setShowPassword] = useState(false);

  const passwordChecks = {
    length: password.length >= 8,
    hasLetter: /[a-zA-Zא-ת]/.test(password),
    hasNumber: /[0-9]/.test(password),
    hasSpecial: /[^a-zA-Z0-9א-ת\s]/.test(password),
  };
  const passwordStrong = passwordChecks.length && passwordChecks.hasLetter && passwordChecks.hasNumber && passwordChecks.hasSpecial;

  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const safeName = fullName.replace(/<[^>]*>/g, '').trim().slice(0, 100);
      if (!safeName) { setError("יש להזין שם תקין"); setLoading(false); return; }

      const { error } = await supabase.auth.signUp({
        email: email.trim().toLowerCase(),
        password,
        options: {
          data: {
            full_name: safeName,
          },
          emailRedirectTo: window.location.origin,
        },
      });

      if (error) throw error;
      
      // Update: Redirect to verification page instead of home
      navigate("/verification-pending", { state: { email } });
      
    } catch (err: any) {
      if (err.message?.includes("already registered")) {
        setError("כתובת האימייל כבר רשומה במערכת. נסה להתחבר.");
      } else {
        setError("אירעה שגיאה ביצירת החשבון. נסה שוב מאוחר יותר.");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white flex" dir="rtl">

      {/* Right side — branding panel (Apple style) */}
      <div className="hidden lg:flex lg:w-[45%] bg-slate-900 relative overflow-hidden flex-col justify-between p-16">
        <div className="relative z-10">
          <Link to="/" className="text-white text-2xl font-bold tracking-tighter">
            {APP_NAME}
          </Link>
        </div>

        <div className="relative z-10 space-y-10">
          <h2 className="text-5xl font-bold text-white leading-tight tracking-tight">
            תתחילו ליצור.<br />
            היום.
          </h2>
          
          <div className="space-y-8">
            {[
              { icon: Globe, text: "גלו מערכות חדשות ומקוריות מכל העולם" },
              { icon: Heart, text: "קראו ביקורות ושתפו מהחוויה האישית שלכם" },
              { icon: Zap, text: "פרסמו את המערכת שלכם לקהל יעד ממוקד" },
            ].map((item, i) => (
              <div key={i} className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-2xl bg-white/5 flex items-center justify-center shrink-0 border border-white/10">
                  <item.icon className="w-5 h-5 text-blue-400" />
                </div>
                <span className="text-slate-300 text-lg leading-snug font-light">{item.text}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="relative z-10 text-xs text-slate-600 font-medium">
          © {new Date().getFullYear()} {APP_NAME}. Join the revolution.
        </div>

        <div className="absolute top-[-10%] right-[-10%] w-[600px] h-[600px] bg-blue-600/10 rounded-full blur-[120px]" />
      </div>

      {/* Left side — form */}
      <div className="flex-1 flex flex-col items-center justify-center p-8 lg:p-16">
        <div className="w-full max-w-[420px] animate-in fade-in slide-in-from-left-4 duration-700">

          <div className="lg:hidden mb-12">
            <Link to="/" className="text-2xl font-bold text-slate-900 tracking-tighter">
              {APP_NAME}
            </Link>
          </div>

          <Link 
            to="/" 
            className="inline-flex items-center text-sm font-bold text-blue-600 hover:text-blue-700 mb-12 transition-colors"
          >
            <ArrowRight className="h-4 w-4 ms-2" />
            חזרה לדף הבית
          </Link>
          
          <div className="mb-10">
            <h1 className="text-4xl font-bold text-slate-900 tracking-tight mb-3">יצירת חשבון.</h1>
            <p className="text-slate-500 text-lg">הצטרפו לקהילת היוצרים של {APP_NAME}.</p>
          </div>

          <form onSubmit={handleRegister} className="space-y-6">
            {error && (
              <Alert variant="destructive" className="bg-red-50 text-red-900 border-none rounded-2xl py-4">
                <AlertDescription className="font-medium">{error}</AlertDescription>
              </Alert>
            )}

            <div className="space-y-2">
              <Label htmlFor="fullName" className="text-xs font-bold text-slate-400 uppercase tracking-widest mr-1">שם מלא</Label>
              <Input
                id="fullName"
                type="text"
                placeholder="ישראל ישראלי"
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                required
                maxLength={100}
                className="h-14 rounded-2xl border-slate-200 bg-slate-50/50 focus:bg-white focus:ring-4 focus:ring-blue-50 transition-all shadow-none text-lg px-5"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="email" className="text-xs font-bold text-slate-400 uppercase tracking-widest mr-1">כתובת אימייל</Label>
              <Input
                id="email"
                type="email"
                placeholder="name@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="h-14 rounded-2xl border-slate-200 bg-slate-50/50 focus:bg-white focus:ring-4 focus:ring-blue-50 transition-all shadow-none text-lg px-5"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="text-xs font-bold text-slate-400 uppercase tracking-widest mr-1">סיסמה</Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  minLength={8}
                  className="h-14 rounded-2xl border-slate-200 bg-slate-50/50 focus:bg-white focus:ring-4 focus:ring-blue-50 transition-all shadow-none pe-14 text-lg px-5"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute inset-y-0 end-0 pe-5 flex items-center text-slate-400 hover:text-slate-600 transition-colors"
                >
                  {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                </button>
              </div>

              {password.length > 0 && (
                <div className="flex flex-wrap gap-x-4 gap-y-2 pt-2 px-1">
                  {[
                    { ok: passwordChecks.length, label: "8+ תווים" },
                    { ok: passwordChecks.hasLetter, label: "אותיות" },
                    { ok: passwordChecks.hasNumber, label: "מספרים" },
                    { ok: passwordChecks.hasSpecial, label: "תו מיוחד" },
                  ].map((check, i) => (
                    <span key={i} className={`text-[10px] font-bold uppercase tracking-wider flex items-center gap-1.5 transition-colors ${check.ok ? 'text-emerald-600' : 'text-slate-300'}`}>
                      <div className={`w-1.5 h-1.5 rounded-full ${check.ok ? 'bg-emerald-500' : 'bg-slate-200'}`} />
                      {check.label}
                    </span>
                  ))}
                </div>
              )}
            </div>

            <Button 
              type="submit" 
              className="w-full h-14 rounded-full bg-slate-900 hover:bg-black text-white font-bold text-lg shadow-xl shadow-slate-100 transition-all active:scale-[0.98] mt-4" 
              disabled={loading || (password.length > 0 && !passwordStrong)}
            >
              {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : "צור חשבון"}
            </Button>

            <div className="relative my-6">
              <div className="absolute inset-0 flex items-center">
                <span className="w-full border-t border-slate-100" />
              </div>
              <div className="relative flex justify-center text-xs uppercase">
                <span className="bg-white px-2 text-slate-400">או</span>
              </div>
            </div>

            <GoogleAuthButton text="הרשמה עם Google" />

            <p className="text-[11px] text-slate-400 text-center leading-relaxed font-medium px-4 mt-4">
              בלחיצה על "צור חשבון" או "הרשמה עם Google" אתה מסכים ל<Link to="/terms" className="underline text-slate-900">תנאי השימוש</Link> ול<Link to="/privacy" className="underline text-slate-900">מדיניות הפרטיות</Link> שלנו.
            </p>
          </form>

          <div className="mt-12 text-center pt-8 border-t border-slate-100">
            <p className="text-slate-500">
              כבר יש לך חשבון?{" "}
              <Link to="/login" className="text-blue-600 font-bold hover:underline">
                התחבר כאן
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Register;